import { useCallback, useEffect, useState } from 'react';
import { api } from './api.js';

export const NOTIFICATIONS_EVENT = 'heatwatch:notifications';
const POLL_MS = 60_000;

/** Where a notification leads: the map, centred on its site or route. */
export const notificationHref = (n) => (n.asset.type === 'route' ? `/?route=${n.asset.id}` : `/?site=${n.asset.id}`);

export const STATE_LABEL = { new: 'New', read: 'Read', resolved: 'Resolved', expired: 'Expired' };
export const CLASS_SHORT = { official: 'Official', threshold: 'App threshold', regulatory: 'Regulatory' };

/** Tell every bell/page to refresh (after marking read elsewhere). */
export const announceNotifications = () => window.dispatchEvent(new Event(NOTIFICATIONS_EVENT));

/** Unread count for the bell: polls while the tab is visible, refreshes on demand. */
export function useUnreadCount(enabled) {
  const [unread, setUnread] = useState(0);
  const refresh = useCallback(() => {
    if (!enabled) return;
    api('/notifications/unread-count')
      .then((r) => setUnread(r.unread ?? 0))
      .catch(() => {});
  }, [enabled]);
  useEffect(() => {
    if (!enabled) {
      setUnread(0);
      return undefined;
    }
    refresh();
    const t = setInterval(() => document.visibilityState === 'visible' && refresh(), POLL_MS);
    window.addEventListener(NOTIFICATIONS_EVENT, refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      clearInterval(t);
      window.removeEventListener(NOTIFICATIONS_EVENT, refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [enabled, refresh]);
  return [unread, setUnread, refresh];
}

export async function markRead(ids) {
  const r = await api('/notifications/read', { method: 'POST', body: { ids } });
  announceNotifications();
  return r;
}

export async function markAllRead() {
  const r = await api('/notifications/read-all', { method: 'POST', body: {} });
  announceNotifications();
  return r;
}

/** The account's notification settings (stored with its preferences). */
export function useNotificationSettings() {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    let live = true;
    Promise.all([api('/notifications/options'), api('/auth/preferences')])
      .then(([opts, p]) => live && setState({ options: opts, settings: { ...opts.defaults, ...(p.preferences?.notifications ?? {}) } }))
      .catch((err) => live && setState({ error: err.message }));
    return () => {
      live = false;
    };
  }, []);
  const save = async (patch) => {
    const next = { ...state.settings, ...patch };
    setState((s) => ({ ...s, settings: next, saving: true, saveError: null }));
    try {
      await api('/auth/preferences', { method: 'PATCH', body: { notifications: patch } });
      setState((s) => ({ ...s, saving: false }));
    } catch (err) {
      setState((s) => ({ ...s, saving: false, saveError: err.message }));
    }
  };
  return { ...state, save };
}
